"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, GroupBot, GroupBotTrace } from "@theta/api-client";
import { thetaApi, dataOf } from "@/lib/api/theta";
import { retryUnlessRefused } from "./queryRetry";

type Schemas = components["schemas"];

function groupBotKey(ref: string) {
  return ["v1", "group", ref, "bot"] as const;
}

function groupBotTracesKey(ref: string) {
  return ["v1", "group", ref, "bot", "traces"] as const;
}

/**
 * THE 404 IS THE ANSWER, AND IT MEANS "NOT IN THE EXPERIMENT"
 *
 * Group bots are off for every group until an operator turns them on, and
 * `modules/bots/gate.ts` answers 404 rather than 403 for a group nobody has —
 * deliberately, so a group admin cannot discover an experiment they were not
 * included in. This hook is therefore the client's only honest test for "does
 * this group have a bot surface at all": `isSuccess` is yes, an error is no.
 * `retryUnlessRefused` keeps that from costing three requests to learn.
 *
 * `enabled` exists so the sidebar can ask only for a group the viewer
 * administers — the one place the answer changes what is drawn.
 */
export function useGroupBot(ref: string, enabled = true) {
  return useQuery({
    queryKey: groupBotKey(ref),
    queryFn: () => thetaApi.http.GET("/groups/{ref}/bot", { params: { path: { ref } } }).then(dataOf<GroupBot>),
    enabled: enabled && ref.length > 0,
    retry: retryUnlessRefused,
    staleTime: 30 * 1000,
  });
}

/** Crude, admin-facing debug log — see the route's own description for why this isn't live. */
export function useGroupBotTraces(ref: string, enabled: boolean) {
  return useQuery({
    queryKey: groupBotTracesKey(ref),
    queryFn: () =>
      thetaApi.http
        .GET("/groups/{ref}/bot/traces", { params: { path: { ref } } })
        .then(dataOf<GroupBotTrace[]>),
    enabled: enabled && ref.length > 0,
    retry: retryUnlessRefused,
    staleTime: 10 * 1000,
  });
}

export function useCreateGroupBot(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas["CreateGroupBotBodyInput"]) =>
      thetaApi.http.POST("/groups/{ref}/bot", { params: { path: { ref } }, body }).then(dataOf<GroupBot>),
    onSuccess: (bot) => queryClient.setQueryData(groupBotKey(ref), bot),
  });
}

export function useUpdateGroupBot(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas["UpdateGroupBotBodyInput"]) =>
      thetaApi.http.PATCH("/groups/{ref}/bot", { params: { path: { ref } }, body }).then(dataOf<GroupBot>),
    onSuccess: (bot) => queryClient.setQueryData(groupBotKey(ref), bot),
  });
}

export function useSetGroupBotEnabled(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      thetaApi.http
        .POST(`/groups/{ref}/bot/${enabled ? "enable" : "disable"}`, {
          params: { path: { ref } },
        })
        .then(dataOf<GroupBot>),
    onSuccess: (bot) => queryClient.setQueryData(groupBotKey(ref), bot),
  });
}
